/**
 * Email List Component
 * Displays list of emails in a table format
 */

import React from 'react';
import {
  Box,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Typography,
  Chip,
  IconButton,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AttachFile as AttachFileIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import { EmailData } from '../../services/emailService';

// Styled components
const StyledListItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.light
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light + '20',
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light + '30'
    }
  }
}));

const UnreadIndicator = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  marginRight: theme.spacing(1)
}));

const EmailSubject = styled(Typography)<{ isUnread?: boolean }>(({ theme, isUnread }) => ({
  fontWeight: isUnread ? 600 : 400,
  color: isUnread ? theme.palette.text.primary : theme.palette.text.secondary
}));

const EmailPreview = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

interface EmailListProps {
  emails: EmailData[];
  selectedEmail: EmailData | null;
  onEmailSelect: (email: EmailData) => void;
  onEmailUpdate: (emailId: string, updates: Partial<EmailData>) => void;
}

const EmailList: React.FC<EmailListProps> = ({
  emails,
  selectedEmail,
  onEmailSelect,
  onEmailUpdate
}) => {

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getInitials = (email: string) => {
    if (!email) return 'NA';
    const parts = email.split('@')[0].split('.');
    return parts.map(part => part.charAt(0).toUpperCase()).join('').slice(0, 2);
  };

  const handleStarToggle = (e: React.MouseEvent, email: EmailData) => {
    e.stopPropagation();
    onEmailUpdate(email.id, { is_starred: !email.is_starred });
  };

  const handleArchive = (e: React.MouseEvent, email: EmailData) => {
    e.stopPropagation();
    onEmailUpdate(email.id, { folder: 'archive' });
  };

  const handleDelete = (e: React.MouseEvent, email: EmailData) => {
    e.stopPropagation();
    onEmailUpdate(email.id, { folder: 'trash' });
  };

  return (
    <List sx={{ width: '100%', padding: 0 }}>
      {Array.isArray(emails) && emails.map((email, index) => (
        <React.Fragment key={email.id}>
          <StyledListItem
            selected={selectedEmail?.id === email.id}
            onClick={() => onEmailSelect(email)}
            sx={{ padding: 2 }}
          >
            {/* Unread indicator */}
            {email.status === 'unread' && (
              <UnreadIndicator />
            )}

            {/* Avatar */}
            <ListItemIcon sx={{ minWidth: 48 }}>
              <Avatar
                sx={{ 
                  width: 32, 
                  height: 32,
                  backgroundColor: email.direction === 'inbound' ? 'primary.main' : 'secondary.main',
                  fontSize: '0.75rem'
                }}
              >
                {getInitials(email.from_address)}
              </Avatar>
            </ListItemIcon>

            {/* Email content */}
            <ListItemText
              sx={{ flex: 1, minWidth: 0 }}
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <EmailSubject 
                    variant="body2" 
                    isUnread={email.status === 'unread'}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    {email.subject || '(No Subject)'}
                  </EmailSubject>
                  
                  {/* Labels */}
                  {email.labels && email.labels.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {email.labels.slice(0, 2).map((label) => (
                        <Chip
                          key={label}
                          label={label}
                          size="small"
                          sx={{ 
                            height: 16, 
                            fontSize: '0.625rem',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      ))}
                      {email.labels.length > 2 && (
                        <Typography variant="caption" color="text.secondary">
                          +{email.labels.length - 2}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {email.direction === 'inbound' ? 'From:' : 'To:'} {email.from_address || 'Unknown'}
                    </Typography>
                    
                    {/* Attachment indicator */}
                    {email.attachments && email.attachments.length > 0 && (
                      <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                    
                    {/* Entity link indicator */}
                    {email.linked_entity_type && (
                      <Chip
                        label={`${email.linked_entity_type}: ${email.linked_entity_id}`}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          height: 16, 
                          fontSize: '0.625rem',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    )}
                  </Box>
                  
                  <EmailPreview>
                    {email.body_text 
                      ? `${email.body_text.slice(0, 120)}...`
                      : email.body_html 
                        ? `${email.body_html.replace(/<[^>]*>/g, '').slice(0, 120)}...`
                        : 'No preview available'
                    }
                  </EmailPreview>
                </Box>
              }
            />

            {/* Actions and date */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-end',
              gap: 1,
              minWidth: 100
            }}>
              <Typography variant="caption" color="text.secondary">
                {formatDate(email.email_date)}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={email.is_starred ? 'Unstar' : 'Star'}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleStarToggle(e, email)}
                    sx={{ padding: 0.5 }}
                  >
                    {email.is_starred ? (
                      <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                  </IconButton>
                </Tooltip>
                
                {email.folder !== 'archive' && (
                  <Tooltip title="Archive">
                    <IconButton
                      size="small"
                      onClick={(e) => handleArchive(e, email)}
                      sx={{ padding: 0.5 }}
                    >
                      <ArchiveIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {email.folder !== 'trash' && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(e, email)}
                      sx={{ padding: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </StyledListItem>
          
          {index < emails.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

export default EmailList;
